import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Popup } from "semantic-ui-react";

import { ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { DuplicateMediumModal, EmailTemplateEditorModal, SmsEditorModal } from "modules/CommunicationTemplates/components";
import "modules/CommunicationTemplates/components/CommunicationTemplatesTable/CommunicationTemplatesTable.scss";
import { CommunicationStatusIcon, DuplicateTemplateButton } from "modules/CommunicationTemplates/components/CommunicationTemplatesTable/components";
import { useCommunicationTemplatesTableHandlers } from "modules/CommunicationTemplates/components/CommunicationTemplatesTable/hooks";
import { useCommunicationMediumsQuery } from "modules/CommunicationTemplates/hooks";
import { stripHtml } from "modules/CommunicationTemplates/util";

import type { Row } from "@tanstack/react-table";
import type { GroupedCommunicationMedium } from "modules/CommunicationTemplates/queryKeys";

const columnHelper = createColumnHelper<GroupedCommunicationMedium>();

export const CommunicationTemplatesTable = () => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [editorModalMedium, setEditorModalMedium] = useState<GroupedCommunicationMedium | null>(null);
  const [editorModalVariation, setEditorModalVariation] = useState<GroupedCommunicationMedium | null>(null);

  const { getCommunicationType, getCommunicationChannel } = useCommunicationTemplatesTableHandlers();
  const { data: communicationMediums, isFetching: isMediumsFetching, refetch: refetchCommunicationMediums } = useCommunicationMediumsQuery();

  useRegisterNavAction(refetchCommunicationMediums);

  const handleToggleExpanded = (e: React.MouseEvent<HTMLButtonElement>, toggleExpanded: (expanded?: boolean) => void) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const handleExpandDuplicatedMedium = () => {
    if (!editorModalVariation || !communicationMediums) return;

    const mediumIdx = communicationMediums.findIndex(medium => medium.communication_medium_id === editorModalVariation.communication_medium_id);
    if (mediumIdx < 0) return;

    setExpandedRows(prev => ({ ...prev, [mediumIdx]: true }));
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "communication_medium_type",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_communication_type", "Communication Type")}</span>,
        size: 120,
        cell: ({
          row: {
            original: { variations, acses_communication_event_type, keylocker_communication_event_type, event_type, channel },
            toggleExpanded,
            getIsExpanded
          }
        }) => (
          <span className="no-wrap-text">
            {variations !== undefined && variations.length > 0 && (
              <button className="CommunicationTemplatesTable__table-expand-button" onClick={e => handleToggleExpanded(e, toggleExpanded)}>
                {getIsExpanded() ? <Icon className="caret down" /> : <Icon className="caret right" />}
              </button>
            )}
            <CommunicationStatusIcon
              channel={channel}
              event_type={event_type}
              acses_communication_event_type={acses_communication_event_type}
              keylocker_communication_event_type={keylocker_communication_event_type}
            />{" "}
            {getCommunicationType({ acses_communication_event_type, event_type, keylocker_communication_event_type })}
          </span>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_channel",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_channel", "Channel")}</span>,
        size: 60,
        cell: ({ row: { original: medium } }) => <span className="no-wrap-text">{getCommunicationChannel(medium.channel)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_brand",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_brand", "Brand")}</span>,
        size: 50,
        cell: ({ row: { original: medium } }) => <span className="no-wrap-text"> {medium.car_brand} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_model",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_model", "Model")}</span>,
        size: 110,
        cell: ({ row: { original: medium } }) => <span className="no-wrap-text"> {medium.car_model} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_subject",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_subject", "Subject")}</span>,
        size: 260,
        cell: ({ row: { original: medium } }) => <span className="no-wrap-text"> {medium.subject} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_message",
        header: () => <span className="no-wrap-text CommunicationTemplatesTable__table-header-text">{t("v8_message", "Message")}</span>,
        size: 420,
        cell: ({ row: { original: medium } }) => (
          <Popup
            hoverable
            content={medium.message}
            trigger={
              <div className="CommunicationTemplatesTable__message-container">
                <p className="CommunicationTemplatesTable__table-ellipsis"> {stripHtml(medium.message)} </p>
              </div>
            }
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "communication_medium_duplicate",
        header: "",
        size: 50,
        cell: ({ row: { original: medium } }) => <DuplicateTemplateButton onClick={() => setEditorModalVariation(medium)} />
      })
    ],
    [t, handleToggleExpanded, getCommunicationType, getCommunicationChannel]
  );

  return (
    <>
      <div className="CommunicationTemplatesTable">
        <ReactTable
          isFetching={isMediumsFetching}
          columns={columns}
          data={communicationMediums}
          onRowClick={setEditorModalMedium}
          expanded={expandedRows}
          onExpandedChange={setExpandedRows}
          renderExpandedComponent={(row: Row<GroupedCommunicationMedium>) =>
            row.original.variations?.length ? <ReactTable columns={columns.slice(0, -1)} data={row.original.variations} onRowClick={setEditorModalMedium} /> : null
          }
        />
      </div>

      <SmsEditorModal onClose={() => setEditorModalMedium(null)} medium={editorModalMedium} />
      <EmailTemplateEditorModal onClose={() => setEditorModalMedium(null)} medium={editorModalMedium} />
      <DuplicateMediumModal onClose={() => setEditorModalVariation(null)} onExpandDuplicatedMedium={handleExpandDuplicatedMedium} medium={editorModalVariation} />
    </>
  );
};
